import React from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, SkeletonRows, EmptyState } from "@/components/dash/ui";
import { VerificationBadge, ApprovalBadge, AccountBadge } from "@/components/dash/PartnerBadges";
import { Input } from "@/components/ui/input";
import { Building2, Search } from "lucide-react";

const TYPE_TABS = [
  { key: "all", label: "All Partners" },
  { key: "client", label: "Clients" },
  { key: "vendor", label: "Vendors" },
  { key: "staffing", label: "Staffing" },
];
const STATUS_VIEWS = [
  { key: "all", label: "All" },
  { key: "needs_review", label: "Needs Review", match: (p) => ["UNDER_REVIEW", "VERIFICATION_SUBMITTED"].includes(p.onboarding_state) },
  { key: "information_required", label: "Information Required", match: (p) => p.onboarding_state === "INFORMATION_REQUIRED" },
  { key: "incomplete", label: "Incomplete", match: (p) => ["REGISTERED", "PROFILE_INCOMPLETE", "DOCUMENTS_REQUIRED"].includes(p.onboarding_state) },
  { key: "approved", label: "Approved", match: (p) => ["VERIFIED", "APPROVED", "ACTIVE"].includes(p.onboarding_state) },
  { key: "rejected", label: "Rejected", match: (p) => p.onboarding_state === "REJECTED" },
  { key: "suspended", label: "Suspended", match: (p) => p.account_status === "suspended" },
];

function Ring({ pct }) {
  const color = pct >= 100 ? "text-emerald-500" : pct >= 60 ? "text-amber-500" : "text-rose-500";
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-8 w-8">
        <svg viewBox="0 0 36 36" className="h-8 w-8 -rotate-90">
          <circle cx="18" cy="18" r="15" fill="none" className="stroke-slate-100" strokeWidth="4" />
          <circle cx="18" cy="18" r="15" fill="none" className={`${color} stroke-current`} strokeWidth="4"
            strokeDasharray={`${(pct / 100) * 94.2} 94.2`} strokeLinecap="round" />
        </svg>
      </div>
      <span className="text-sm font-semibold text-slate-700">{pct}%</span>
    </div>
  );
}

export default function PartnersList() {
  const nav = useNavigate();
  const [items, setItems] = React.useState(null);
  const [type, setType] = React.useState("all");
  const [view, setView] = React.useState("all");
  const [q, setQ] = React.useState("");

  React.useEffect(() => {
    setItems(null);
    const params = {};
    if (type !== "all") params.type = type;
    if (q) params.q = q;
    api.get("/admin/onboarding/partners", { params }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [type, q]);

  const viewDef = STATUS_VIEWS.find((v) => v.key === view);
  const filtered = (items || []).filter((p) => (view === "all" || !viewDef?.match ? true : viewDef.match(p)));

  return (
    <div>
      <PageHeader title="Partners" description="Client, Vendor and Staffing Partner verification & management." breadcrumb="Partner Management / Partners" />
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="partners-type-tabs">
        {TYPE_TABS.map((t) => (
          <button key={t.key} onClick={() => setType(t.key)} data-testid={`partners-type-${t.key}`}
            className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${type === t.key ? "border-vs-royal bg-vs-light text-vs-royal" : "border-slate-200 bg-white text-slate-500"}`}>{t.label}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search partners…" className="pl-9 w-56" data-testid="partners-search" />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="partners-status-views">
        {STATUS_VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} data-testid={`partners-view-${v.key}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${view === v.key ? "border-vs-royal bg-vs-royal text-white" : "border-slate-200 bg-white text-slate-500"}`}>{v.label}</button>
        ))}
      </div>

      <Card className="!p-0">
        {items === null ? <div className="p-5"><SkeletonRows /></div> : filtered.length === 0 ? (
          <EmptyState icon="Building2" title="No partners found" description="Try a different filter or search term." />
        ) : (
          <div className="overflow-x-auto min-w-0">
            <table className="w-full min-w-[920px] text-sm" data-testid="partners-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3 font-semibold">Partner</th><th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Profile</th><th className="px-4 py-3 font-semibold">Documents</th>
                <th className="px-4 py-3 font-semibold">Verification</th><th className="px-4 py-3 font-semibold">Approval</th>
                <th className="px-4 py-3 font-semibold">Account</th><th className="px-4 py-3 font-semibold">Registered</th>
              </tr></thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} onClick={() => nav(`/app/partners/${p.id}`)} data-testid={`partner-row-${p.id}`}
                    className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-vs-light flex items-center justify-center shrink-0 overflow-hidden">
                          {p.logo ? <img src={p.logo} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-4 w-4 text-vs-royal" />}
                        </div>
                        <div className="min-w-0"><div className="font-semibold text-slate-800 truncate">{p.name}</div>
                          <div className="text-xs text-slate-400">{p.partner_code}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-600">{p.type}</td>
                    <td className="px-4 py-3"><Ring pct={p.profile_completion} /></td>
                    <td className="px-4 py-3 text-slate-700">{p.document_uploaded} / {p.document_required}</td>
                    <td className="px-4 py-3"><VerificationBadge status={p.verification_status} /></td>
                    <td className="px-4 py-3"><ApprovalBadge status={p.approval_status} /></td>
                    <td className="px-4 py-3"><AccountBadge status={p.account_status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
